import { z } from 'zod'

export const fontSizeSchema = z.enum(['sm', 'md', 'lg', 'xl', 'xxl'])
export type FontSize = z.infer<typeof fontSizeSchema>

export const fontFamilySchema = z.enum(['system', 'lexend', 'dyslexic'])
export type FontFamily = z.infer<typeof fontFamilySchema>

export const spacingSchema = z.enum(['normal', 'wide'])
export type Spacing = z.infer<typeof spacingSchema>

export const contrastModeSchema = z.enum(['normal', 'high', 'bw', 'invert', 'sepia'])
export type ContrastMode = z.infer<typeof contrastModeSchema>

export const colorBlindFilterSchema = z.enum(['none', 'protanopia', 'deuteranopia', 'tritanopia'])
export type ColorBlindFilter = z.infer<typeof colorBlindFilterSchema>

export const AccessibilitySettingsSchema = z.object({
  fontSize: fontSizeSchema,
  fontFamily: fontFamilySchema,
  letterSpacing: spacingSchema,
  wordSpacing: spacingSchema,
  lineHeight: spacingSchema,
  contrastMode: contrastModeSchema,
  reducedMotion: z.boolean(),
  autoplayVideo: z.boolean(),
  colorBlindFilter: colorBlindFilterSchema,
  enhancedFocus: z.boolean(),
  ttsEngine: z.enum(['browser', 'edge']),
  ttsRate: z.number().min(0.5).max(2.0),
  ttsVoiceName: z.string().nullable(),
  ttsKaraoke: z.boolean(),
  easyReadDefault: z.boolean(),
  captionsDefault: z.boolean(),
  signLanguagePreferred: z.boolean(),
  distractionFree: z.boolean(),
})

export type AccessibilitySettings = z.infer<typeof AccessibilitySettingsSchema>

export const DEFAULT_ACCESSIBILITY_SETTINGS: AccessibilitySettings = {
  fontSize: 'md',
  fontFamily: 'lexend',
  letterSpacing: 'normal',
  wordSpacing: 'normal',
  lineHeight: 'normal',
  contrastMode: 'normal',
  reducedMotion: false,
  autoplayVideo: false,
  colorBlindFilter: 'none',
  enhancedFocus: false,
  ttsEngine: 'edge',
  ttsRate: 1.0,
  ttsVoiceName: null,
  ttsKaraoke: true,
  easyReadDefault: false,
  captionsDefault: false,
  signLanguagePreferred: false,
  distractionFree: false,
}

export const A11Y_COOKIE_NAME = 'a11y-settings'
export const A11Y_STORAGE_KEY = 'a11y-settings'

export function parseSettings(raw: unknown): AccessibilitySettings {
  if (raw == null) return DEFAULT_ACCESSIBILITY_SETTINGS

  let value: unknown = raw
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw)
    } catch {
      return DEFAULT_ACCESSIBILITY_SETTINGS
    }
  }

  const merged = {
    ...DEFAULT_ACCESSIBILITY_SETTINGS,
    ...(typeof value === 'object' && value !== null ? value : {}),
  }

  const parsed = AccessibilitySettingsSchema.safeParse(merged)
  return parsed.success ? parsed.data : DEFAULT_ACCESSIBILITY_SETTINGS
}
